// Status-grouped section of application cards
// Shows section header with title + optional add button

import type {
  Application,
  ApplicationStatus,
  UpdateApplicationPayload,
} from '@startus/applications';
import {
  ApplicationCard,
  type ApplicationCardLabels,
  type CountryOption,
  type EditorInfo,
} from './ApplicationCard';

interface ApplicationGroupProps {
  title: string;
  status: ApplicationStatus;
  applications: Application[];
  expandedId: string | null;
  onToggle: (id: string) => void;
  mode?: 'edit' | 'view';
  onSave?: (id: string, updates: UpdateApplicationPayload) => Promise<void>;
  onDelete?: (id: string) => void;
  cardLabels: ApplicationCardLabels;
  countryOptions?: CountryOption[];
  editorMap?: Map<string, EditorInfo>;
  savingId?: string | null;
}

export function ApplicationGroup({
  title,
  applications,
  expandedId,
  onToggle,
  mode = 'edit',
  onSave,
  onDelete,
  cardLabels,
  countryOptions = [],
  editorMap = new Map(),
  savingId = null,
}: ApplicationGroupProps) {
  return (
    <div className="border border-white/5 rounded-xl p-4">
      {/* Group header */}
      <div className="mb-3">
        <h2 className="font-semibold text-base">{title}</h2>
      </div>

      {/* Cards — inset dividers handled inside ApplicationCard */}
      <div>
        {applications.map((app, idx) => (
          <ApplicationCard
            key={app.id}
            application={app}
            expanded={expandedId === app.id}
            onToggle={() => onToggle(app.id)}
            mode={mode}
            onSave={onSave}
            onDelete={onDelete}
            labels={cardLabels}
            countryOptions={countryOptions}
            editorInfo={app.lastEditedBy ? editorMap.get(app.lastEditedBy) : undefined}
            loading={savingId === app.id}
            isLast={idx === applications.length - 1}
          />
        ))}
      </div>
    </div>
  );
}
